/**
 * MODULE: domain/pages/frontend/components/editor/PagesPipelineSnapshotBlockView.tsx
 * PURPOSE: Live CRM pipeline KPI + stage funnel embedded in a note page
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'flowbite-react';
import { HiOutlineChartBar, HiOutlineExternalLink, HiOutlinePencil, HiOutlineRefresh } from 'react-icons/hi';
import { useIsPagesPublicView } from '../../context/PagesEditorViewModeContext';
import { useTenantDefaultCurrency } from '@shared/lib/frontend/hooks/useTenantDefaultCurrency';
import { usePipelineSnapshot } from '../../hooks/usePipelineSnapshot';
import {
  formatPipelineCount,
  formatPipelineCurrency,
  pipelineFunnelMaxCount,
  pipelineSnapshotContentToAttrs,
  readPipelineSnapshotFromAttrs,
} from '../../utils/pagePipelineSnapshotUtils';
import { PipelineSnapshotConfigModal } from './PipelineSnapshotConfigModal';

export function PagesPipelineSnapshotBlockView({ node, selected, updateAttributes, editor }: NodeViewProps) {
  const navigate = useNavigate();
  const isPublicView = useIsPagesPublicView();
  const editable = editor.isEditable && !isPublicView;
  const [configOpen, setConfigOpen] = useState(false);

  const content = readPipelineSnapshotFromAttrs(node.attrs as Record<string, unknown>);
  const { data, isLoading, isError, refetch, isFetching } = usePipelineSnapshot({
    enabled: !isPublicView,
  });

  const { currency: tenantCurrency } = useTenantDefaultCurrency();
  const currencyCode = data?.kpis.currencyCode || tenantCurrency;
  const funnelMax = useMemo(
    () => pipelineFunnelMaxCount(data?.stageCounts ?? []),
    [data?.stageCounts]
  );

  const ringClass = selected ? 'ring-2 ring-blue-400 ring-offset-2 dark:ring-offset-gray-900' : '';

  return (
    <NodeViewWrapper className="my-3" data-testid="pages-pipeline-snapshot-block">
      <div
        className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900 ${ringClass}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white px-4 py-3 dark:border-gray-800 dark:from-blue-950/30 dark:to-gray-900">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200">
              <HiOutlineChartBar className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <p
                className="truncate text-sm font-semibold text-gray-900 dark:text-white"
                data-testid="pages-pipeline-snapshot-title"
              >
                {content.title}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Live CRM pipeline</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!isPublicView ? (
              <>
                <Button
                  size="xs"
                  color="light"
                  aria-label="Refresh pipeline snapshot"
                  disabled={isFetching}
                  onClick={() => void refetch()}
                >
                  <HiOutlineRefresh className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  size="xs"
                  color="light"
                  data-testid="pages-pipeline-snapshot-open-crm"
                  onClick={() => navigate('/crm')}
                >
                  <HiOutlineExternalLink className="mr-1 h-4 w-4" />
                  Open CRM
                </Button>
              </>
            ) : null}
            {editable ? (
              <Button
                size="xs"
                color="light"
                aria-label="Configure pipeline snapshot"
                onClick={() => setConfigOpen(true)}
              >
                <HiOutlinePencil className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>

        <div className="px-4 py-4">
          {isPublicView ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Sign in to view live pipeline metrics from your CRM.
            </p>
          ) : isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500 dark:text-gray-400">
              <Spinner size="sm" />
              Loading pipeline snapshot…
            </div>
          ) : isError ? (
            <div className="space-y-2 py-2">
              <p className="text-sm text-red-600 dark:text-red-400">Could not load pipeline data.</p>
              <Button size="xs" color="light" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              {content.show_kpis && data ? (
                <div
                  className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4"
                  data-testid="pages-pipeline-snapshot-kpis"
                >
                  <KpiTile
                    label="Total pipeline"
                    value={formatPipelineCurrency(data.kpis.totalPipeline, currencyCode)}
                    testId="pages-pipeline-snapshot-kpi-total"
                  />
                  <KpiTile
                    label="Active deals"
                    value={formatPipelineCount(data.kpis.activeDeals)}
                    testId="pages-pipeline-snapshot-kpi-deals"
                  />
                  <KpiTile
                    label="Weighted"
                    value={formatPipelineCurrency(data.kpis.weightedPipeline, currencyCode)}
                    testId="pages-pipeline-snapshot-kpi-weighted"
                  />
                  <KpiTile
                    label="At risk"
                    value={formatPipelineCount(data.kpis.dealsAtRisk ?? 0)}
                    testId="pages-pipeline-snapshot-kpi-at-risk"
                  />
                </div>
              ) : null}

              {content.show_funnel && data ? (
                <div data-testid="pages-pipeline-snapshot-funnel">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Open pipeline by stage
                  </p>
                  <div className="space-y-2">
                    {data.stageCounts.map((row) => {
                      const widthPct = row.count === 0 ? 0 : Math.max(8, (row.count / funnelMax) * 100);
                      return (
                        <div
                          key={row.stage}
                          className="grid grid-cols-[minmax(0,7rem)_1fr_2rem] items-center gap-2 text-xs"
                          data-testid={`pages-pipeline-snapshot-stage-${row.stage}`}
                        >
                          <span className="truncate text-gray-600 dark:text-gray-300" title={row.label}>
                            {row.label}
                          </span>
                          <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                            <div
                              className="h-full rounded-full bg-blue-500 transition-all dark:bg-blue-400"
                              style={{ width: `${widthPct}%` }}
                            />
                          </div>
                          <span className="text-right font-medium tabular-nums text-gray-700 dark:text-gray-200">
                            {row.count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {!content.show_kpis && !content.show_funnel ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No sections enabled — use the pencil icon to configure this block.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>

      {editable && configOpen ? (
        <PipelineSnapshotConfigModal
          open={configOpen}
          initial={content}
          onClose={() => setConfigOpen(false)}
          onSubmit={(next) => updateAttributes(pipelineSnapshotContentToAttrs(next))}
        />
      ) : null}
    </NodeViewWrapper>
  );
}

function KpiTile({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div
      className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/60"
      data-testid={testId}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-0.5 truncate text-base font-semibold tabular-nums text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}
